import { Injectable, inject, DestroyRef, signal } from '@angular/core';
import { Subscription } from 'rxjs';

import { ConfigService } from './config.service.js';
import { PointCloudLoaderService } from './pointcloud-loader.service.js';
import { PointCloudApiService } from './pointcloud-api.service.js';
import { ViewerCoreService } from './viewer-core.service.js';

/**
 * Monitors camera proximity to digital twin features and dynamically loads/unloads
 * point clouds as the user navigates the terrain view.
 *
 * While a feature's point cloud is displayed its digital twin overlay geometry
 * is hidden, and it is shown again as soon as the point cloud is unloaded.
 */
@Injectable({ providedIn: 'root' })
export class DynamicPointCloudService {
  private core = inject(ViewerCoreService);
  private config = inject(ConfigService);
  private pointCloudApi = inject(PointCloudApiService);
  private loader = inject(PointCloudLoaderService);
  private destroyRef = inject(DestroyRef);

  /** Currently loaded model GUID (null if none) */
  readonly activeModelId = signal<string | null>(null);

  /** Whether a point cloud is currently being loaded */
  readonly loading = signal(false);

  private tenant: string | null = null;
  private loadSubscription: Subscription | null = null;
  private cameraListenerRemover: (() => void) | null = null;
  private activePointCloud: any = null;
  /** Feature overlay hidden while its point cloud is displayed. */
  private hiddenFeatureGuid: string | null = null;
  private lastCheckTime = 0;
  /**
   * When a model is loaded directly (via modelId query param) it is "pinned":
   * the proximity monitor must never auto-unload it.
   */
  private pinned = false;

  /**
   * Distance from the camera to the point it looks at (in meters) below which
   * proximity detection activates. Above it the user is in overview mode and
   * only the digital twin overlays are shown.
   */
  private readonly ACTIVATION_DISTANCE_M = 6000;
  /** Horizontal distance (m) to a feature at which its point cloud is loaded. */
  private readonly LOAD_DISTANCE_M = 800;
  /**
   * Horizontal distance (m) at which the point cloud is unloaded again. Larger
   * than LOAD_DISTANCE_M so the model does not flicker while the camera hovers
   * around the threshold.
   */
  private readonly UNLOAD_DISTANCE_M = 1500;
  /** Minimum milliseconds between proximity checks */
  private readonly CHECK_INTERVAL_MS = 500;

  constructor() {
    this.destroyRef.onDestroy(() => this.stop());
  }

  /**
   * Start monitoring camera position for feature proximity.
   * If an initial modelId is provided, loads it immediately.
   * Always listens for camera changes to dynamically load/unload point clouds.
   */
  start(tenant: string | null, initialModelId?: string | null): void {
    this.stop();

    // Fall back to the same default tenant the digital twin overlay uses, so
    // proximity loading also works when the URL carries no tenant parameter.
    this.tenant = tenant;

    // Load initial model if provided
    if (initialModelId) {
      this.loadModel(initialModelId, true);
    }

    const viewer = this.core.viewer();
    if (!viewer) return;

    const handler = () => this.onCameraChanged();
    viewer.addEventListener('camera_changed', handler);
    this.cameraListenerRemover = () => viewer.removeEventListener('camera_changed', handler);
  }

  /** Stop monitoring and unload any active point cloud. */
  stop(): void {
    this.cameraListenerRemover?.();
    this.cameraListenerRemover = null;
    this.unloadCurrent();
  }

  private onCameraChanged(): void {
    // A directly-loaded (pinned) model must never be auto-unloaded.
    if (this.pinned) return;
    if (this.loading()) return;

    const now = Date.now();
    if (now - this.lastCheckTime < this.CHECK_INTERVAL_MS) return;
    this.lastCheckTime = now;

    const viewer = this.core.viewer();
    const manager = viewer?.quantizedTerrainManager;
    if (!manager) return;

    const view = viewer.scene?.view;
    if (!view) return;

    // Proximity is measured against the point the camera looks at, not the
    // camera itself: when looking down at an angle the camera can be a long
    // way from the terrain area actually on screen.
    const target = this.toAbsoluteLV95(view.getPivot(), manager);
    if (!target) return;

    // Only active once the user is zoomed in far enough.
    if (view.radius > this.ACTIVATION_DISTANCE_M) {
      this.unloadCurrent();
      return;
    }

    const activeId = this.activeModelId();
    const searchRadius = activeId ? this.UNLOAD_DISTANCE_M : this.LOAD_DISTANCE_M;
    const nearest = manager.findNearestFeature(target.x, target.y, searchRadius);

    if (!nearest) {
      // No feature within range — unload whatever is loaded.
      this.unloadCurrent();
      return;
    }

    // Still close enough to the loaded model: nothing to do.
    if (nearest.guid === activeId) return;

    // Another feature became the nearest one. Only switch to it once it is
    // actually within the load distance, otherwise keep the current model.
    if (nearest.distance > this.LOAD_DISTANCE_M) {
      if (activeId) this.unloadCurrent();
      return;
    }

    // Load the new point cloud (unloads previous)
    this.loadModel(nearest.guid);
  }

  /**
   * Convert a scene position to absolute LV95 metres. The scene may use either
   * absolute LV95 coordinates or coordinates relative to the terrain origin.
   */
  private toAbsoluteLV95(position: any, manager: any): { x: number; y: number } | null {
    if (!position) return null;
    if (Math.abs(position.x) > 1000000 || Math.abs(position.y) > 1000000) {
      return { x: position.x, y: position.y };
    }
    return {
      x: position.x + (manager.lv95Origin?.x ?? 0),
      y: position.y + (manager.lv95Origin?.y ?? 0),
    };
  }

  private async loadModel(modelId: string, fitToScreen = false): Promise<void> {
    this.loading.set(true);

    // Unload any previous point cloud first
    this.unloadCurrent();

    // A directly-requested model (loaded with fitToScreen) is pinned so the
    // proximity monitor never auto-unloads it.
    this.pinned = fitToScreen;

    if (!this.tenant) {
      this.loading.set(false);
      return;
    }

    try {
      const metadata = await this.pointCloudApi.fetchModelMetadata(this.tenant, modelId);

      if (!metadata.saPath) {
        console.warn('Dynamic load: no saPath for model', modelId);
        this.loading.set(false);
        return;
      }

      const modelUrl = this.pointCloudApi.buildModelUrl(metadata.saPath, metadata.format);

      const token = await this.pointCloudApi.activateToken(this.tenant, modelId);
      if (!token) {
        console.warn('Dynamic load: failed to get token for', modelId);
        this.loading.set(false);
        return;
      }

      this.pointCloudApi.startTokenRefresh(this.tenant, modelId);

      // Load point cloud
      this.loadSubscription?.unsubscribe();
      this.loadSubscription = this.loader
        .loadAndAdd(modelUrl, {
          pointSize: 0.6,
          activeAttribute: 'rgba',
          fitToScreen,
        })
        .subscribe({
          next: event => {
            this.activePointCloud = event.pointcloud;
            this.activeModelId.set(modelId);
            this.hideFeatureOverlay(modelId);
            this.loading.set(false);
          },
          error: err => {
            console.error('Dynamic point cloud load failed:', err);
            this.loading.set(false);
          },
        });
    } catch (err) {
      console.error('Dynamic point cloud load failed:', err);
      this.loading.set(false);
    }
  }

  private unloadCurrent(): void {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
    this.pinned = false;

    const hadModel = this.activePointCloud != null || this.activeModelId() != null;

    if (this.activePointCloud) {
      const viewer = this.core.viewer();
      if (viewer?.scene) {
        viewer.scene.removePointCloud(this.activePointCloud);
      }
      this.activePointCloud = null;
    }

    this.restoreFeatureOverlay();

    if (hadModel) {
      this.pointCloudApi.stopTokenRefresh();
      this.activeModelId.set(null);
      this.config.updateSasToken(null);
    }
  }

  /** Hide the digital twin overlay of the feature whose point cloud is shown. */
  private hideFeatureOverlay(guid: string): void {
    const manager = this.core.viewer()?.quantizedTerrainManager;
    if (typeof manager?.setFeatureVisible !== 'function') return;
    manager.setFeatureVisible(guid, false);
    this.hiddenFeatureGuid = guid;
  }

  /** Show the digital twin overlay that was hidden while its model was shown. */
  private restoreFeatureOverlay(): void {
    if (!this.hiddenFeatureGuid) return;
    const manager = this.core.viewer()?.quantizedTerrainManager;
    if (typeof manager?.setFeatureVisible === 'function') {
      manager.setFeatureVisible(this.hiddenFeatureGuid, true);
    }
    this.hiddenFeatureGuid = null;
  }
}
